import { CalendarPlus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import useLocalState from "../lib/useLocalState";
import { parseIso, addCalendarMonth, addWorkingDays, fmtLong, daysUntil } from "../lib/deadlines";

const OVERLINE_CLS = "text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold";
const LABEL_CLS = "block text-xs font-accent uppercase tracking-[0.14em] text-ink-muted mb-1.5";
const INPUT_CLS =
  "w-full bg-white border border-rule px-3 py-2.5 text-sm text-ink focus:outline-none focus:border-ink transition-colors";

function isoOf(date) {
  const p = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}`;
}

function countdown(due) {
  const diff = daysUntil(due);
  if (diff > 1) return { text: `${diff} days left`, overdue: false };
  if (diff === 1) return { text: "due tomorrow", overdue: false };
  if (diff === 0) return { text: "due today", overdue: false };
  const n = -diff;
  return { text: `${n} ${n === 1 ? "day" : "days"} overdue — time to chase`, overdue: true };
}

function escIcs(s) {
  return s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

function downloadIcs({ id, summary, description, due }) {
  const dtStart = isoOf(due);
  const dayAfter = new Date(due);
  dayAfter.setDate(dayAfter.getDate() + 1);
  const stamp = new Date().toISOString().replace(/[-:]/g, "").slice(0, 15) + "Z";
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//You Are Not Alone//truth4parents.com//EN",
    "BEGIN:VEVENT",
    `UID:yana-${id}-${dtStart}@truth4parents.com`,
    `DTSTAMP:${stamp}`,
    `DTSTART;VALUE=DATE:${dtStart}`,
    `DTEND;VALUE=DATE:${isoOf(dayAfter)}`,
    `SUMMARY:${escIcs(summary)}`,
    `DESCRIPTION:${escIcs(description)}`,
    "BEGIN:VALARM",
    "TRIGGER:-P1D",
    "ACTION:DISPLAY",
    `DESCRIPTION:${escIcs(summary + " (tomorrow)")}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${id}-reply-deadline.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DeadlineRow({ id, label, ruleNote, sent, onChange, due }) {
  const status = due ? countdown(due) : null;
  return (
    <div className="border border-rule bg-white p-5 md:p-6">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
        <div className="min-w-[200px]">
          <div className="font-serif text-xl text-ink leading-tight tracking-tight">{label}</div>
          <div className="text-[11px] text-ink-muted font-accent mt-1">{ruleNote}</div>
        </div>
        <div>
          <label className={LABEL_CLS} htmlFor={`deadline-${id}-date`}>
            Date you sent it
          </label>
          <input
            id={`deadline-${id}-date`}
            data-testid={`deadline-${id}-date`}
            type="date"
            value={sent}
            onChange={onChange}
            className={INPUT_CLS + " w-auto"}
          />
        </div>
        {due && (
          <>
            <div className="flex-1 min-w-[220px]">
              <div className={LABEL_CLS.replace("mb-1.5", "mb-1")}>Reply due by</div>
              <div data-testid={`deadline-${id}-due`} className="text-sm text-ink font-accent font-semibold">
                {fmtLong(due)}
              </div>
              <div
                data-testid={`deadline-${id}-days`}
                className={`mt-1 inline-block text-xs font-accent font-semibold px-2.5 py-1 ${
                  status.overdue ? "bg-ember/10 text-ember" : "bg-river/10 text-river"
                }`}
              >
                {status.text}
              </div>
              {status.overdue && (
                <div className="mt-1.5">
                  <Link
                    to="/understanding/records/complaint"
                    data-testid={`deadline-${id}-complaint-link`}
                    className="text-xs font-accent font-semibold text-ember underline underline-offset-2 hover:text-ink"
                  >
                    Build a chase letter &amp; ICO complaint →
                  </Link>
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid={`deadline-${id}-ics`}
              onClick={() =>
                downloadIcs({
                  id,
                  summary:
                    id === "sar"
                      ? "SAR reply due — chase if nothing has arrived"
                      : "FOI reply due — chase if nothing has arrived",
                  description:
                    "If no reply has arrived, chase them in writing today and keep a copy. If they still do not respond, you can complain — how to escalate: https://truth4parents.com/understanding/records",
                  due,
                })
              }
              className="inline-flex items-center gap-2 border border-ink/30 text-ink px-4 py-2 text-xs font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
            >
              <CalendarPlus size={13} strokeWidth={1.75} />
              Add to my calendar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function DeadlineTracker() {
  const [dates, setDates, clearDates] = useLocalState("yana:deadlines:v1", {
    sarSent: "",
    foiSent: "",
  });

  const sarDue = dates.sarSent ? addCalendarMonth(dates.sarSent) : null;
  const foiDue = dates.foiSent ? addWorkingDays(dates.foiSent, 20) : null;
  const hasAny = !!(dates.sarSent || dates.foiSent);

  return (
    <section data-testid="deadline-tracker" className="border border-rule bg-linen-alt p-6 md:p-8">
      <div className={OVERLINE_CLS}>Deadline tracker</div>
      <h2 className="font-serif text-2xl md:text-3xl text-ink mt-2 mb-3 tracking-tight leading-tight">
        So nothing slips.
      </h2>
      <p className="text-sm text-ink-muted leading-relaxed mb-6 max-w-2xl">
        Enter the date you sent each request. We work out the legal deadline, remember it in this
        browser, and show you a countdown whenever you come back. For a nudge that reaches you even
        when this page is closed, add the deadline to your phone or computer calendar — it will
        remind you the day before and on the day.
      </p>
      <div className="space-y-4">
        <DeadlineRow
          id="sar"
          label="Subject Access Request"
          ruleNote="1 calendar month to respond"
          sent={dates.sarSent}
          onChange={(e) => setDates((d) => ({ ...d, sarSent: e.target.value }))}
          due={sarDue}
        />
        <DeadlineRow
          id="foi"
          label="Freedom of Information Request"
          ruleNote="20 working days to respond"
          sent={dates.foiSent}
          onChange={(e) => setDates((d) => ({ ...d, foiSent: e.target.value }))}
          due={foiDue}
        />
      </div>
      <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-[11px] text-ink-muted font-accent leading-relaxed max-w-xl">
          Working days are Monday to Friday. Bank holidays do not count as working days — if one
          falls inside the window, allow an extra day. Saved only in this browser, never sent
          anywhere.
        </p>
        {hasAny && (
          <button
            type="button"
            onClick={clearDates}
            data-testid="deadline-clear"
            className="inline-flex items-center gap-2 text-xs font-accent text-ink-muted hover:text-ember transition-colors"
          >
            <Trash2 size={12} strokeWidth={1.75} />
            Clear my dates
          </button>
        )}
      </div>
    </section>
  );
}
